import { forwardRef, type ReactNode } from 'react';

export type HbsButtonVariant = 'primary' | 'accent' | 'ghost' | 'subtle' | 'plain' | 'danger';

interface HbsButtonProps {
  variant?: HbsButtonVariant;
  type?: 'button' | 'submit';
  /** DOM id — stable hook for tests and for label/aria references. */
  id?: string;
  /** Native hover tooltip. Also surfaces to screen readers as a description,
   *  so keep it a full explanation of what the button does, not a repeat of
   *  the label. */
  title?: string;
  /** Associates a submit button with a form by id, for buttons rendered outside the <form>. */
  form?: string;
  onClick?: () => void;
  disabled?: boolean;
  loading?: boolean;
  fullWidth?: boolean;
  leadingIcon?: ReactNode;
  children: ReactNode;
}

const BASE =
  // `whitespace-nowrap` so a short label ("Turn on") never wraps to two lines when
  // a flex row (e.g. Settings' justify-between rows) squeezes the button — the
  // fixed h-9 can't fit two lines anyway.
  'inline-flex items-center justify-center gap-2 h-9 px-[14px] rounded-hbs-sm text-[13px] whitespace-nowrap transition-colors disabled:opacity-50 disabled:cursor-not-allowed active:scale-[0.97]';

const VARIANT_CLASSES: Record<HbsButtonVariant, string> = {
  primary:
    'font-bold bg-hbs-primary text-hbs-on-primary hover:bg-hbs-primary-hover',
  accent:
    'font-bold bg-hbs-accent text-white hover:opacity-90',
  ghost:
    'font-semibold border border-hbs-border bg-transparent text-hbs-text hover:bg-hbs-surface-alt',
  subtle:
    'font-semibold bg-hbs-surface-alt text-hbs-text hover:bg-hbs-surface',
  plain:
    'font-semibold bg-transparent text-hbs-primary hover:underline',
  danger:
    'font-bold bg-hbs-danger text-white hover:opacity-90',
};

function Spinner(): JSX.Element {
  return (
    <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24" aria-hidden="true">
      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
    </svg>
  );
}

export const HbsButton = forwardRef<HTMLButtonElement, HbsButtonProps>(function HbsButton(
  {
    variant = 'primary',
    type = 'button',
    id,
    title,
    form,
    onClick,
    disabled,
    loading,
    fullWidth,
    leadingIcon,
    children,
  },
  ref,
): JSX.Element {
  return (
    <button
      ref={ref}
      id={id}
      title={title}
      type={type}
      form={form}
      onClick={onClick}
      disabled={disabled || loading}
      className={`${BASE} ${VARIANT_CLASSES[variant]} ${fullWidth ? 'w-full' : ''}`}
    >
      {loading ? <Spinner /> : leadingIcon}
      {children}
    </button>
  );
});

HbsButton.displayName = 'HbsButton';
